import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import { Observable } from 'rxjs';
import { catchError, finalize, map, shareReplay } from 'rxjs/operators';

import { AppOptionsService } from '../app-options';
import { ErrorsHandlingService } from '../errors-handling';
import { HttpUtils } from '../../class';
import { HelpersInterface } from '../../interfaces';

export interface IGroupName { id: number; name: string; region: string }

export interface IGroupWithEndpoints { id: number; name: string; endpoint_ids: HelpersInterface.IGroupEndpoint[] }

@Injectable({ providedIn: 'root' })
export class GroupsService {
  private apiUrl = '';
  private groupsWithEndpointsRequests = new Map<'ssp' | 'dsp' | undefined, Observable<IGroupWithEndpoints[]>>();

  constructor(
    private http: HttpClient,
    private options: AppOptionsService,
    private errorsHandlingService: ErrorsHandlingService,
  ) {
    this.apiUrl = this.options.getApiUrl();
  }

  public getGroupNames(type?: 'ssp' | 'dsp'): Observable<IGroupName[]> {
    const requestParams = HttpUtils.getRequestParams({ type });
    return this.http.get<IGroupName[]>(`${this.apiUrl}/endpoint-groups/list`, { params: requestParams })
      .pipe(catchError(this.errorsHandlingService.processError));
  }

  // /list carries the group names only, the index endpoint also returns the endpoints
  // A single page of 1000 groups is enough, paginate if a partner ever exceeds that
  // Every scanner that opens in the groups view asks for the same list within one tick, so
  // overlapping callers share a single request; once it settles a later call fetches fresh data
  public getGroupsWithEndpoints(type?: 'ssp' | 'dsp'): Observable<IGroupWithEndpoints[]> {
    const pendingRequest = this.groupsWithEndpointsRequests.get(type);

    if (pendingRequest) {
      return pendingRequest;
    }

    const requestParams = HttpUtils.getRequestParams({ type, field: 'name', sortBy: 'asc', rows: 1000 });
    const request = this.http.get<{ data: IGroupWithEndpoints[] }>(`${this.apiUrl}/endpoint-groups`, { params: requestParams })
      .pipe(
        map(res => res.data),
        catchError(this.errorsHandlingService.processError),
        finalize(() => this.groupsWithEndpointsRequests.delete(type)),
        shareReplay({ refCount: true, bufferSize: 1 }),
      );

    this.groupsWithEndpointsRequests.set(type, request);

    return request;
  }

  // The API returns the endpoints as objects, the callers only need their ids
  public getGroupById(id: number): Observable<{ id: number; name: string; type: string; endpoint_ids: number[] }> {
    return this.http.get<{ data: { id: number; name: string; type: string; endpoint_ids: HelpersInterface.IGroupEndpoint[] } }>(`${this.apiUrl}/endpoint-groups/${id}`)
      .pipe(
        map(res => ({ ...res.data, endpoint_ids: res.data.endpoint_ids.map(endpoint => endpoint.id) })),
        catchError(this.errorsHandlingService.processError),
      );
  }
}
